import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useMarketData } from '@/hooks/useMarketData';
import { usePortalContext } from '@/layouts/CustomerPortalLayout';
import { formatPriceCt } from '@/utils/formatters';
import {
  TrendingUp, TrendingDown, Minus, Zap, Sun, Wind, Factory,
  Info, CheckCircle2, AlertCircle, Clock, Database, Award
} from 'lucide-react';

const PortalMarket = () => {
  const { marketData, isLoading, error, refetch } = useMarketData();
  const { data } = usePortalContext();

  if (isLoading && !marketData) {
    return <div className="text-center py-20 text-muted-foreground">Marktdaten werden geladen...</div>;
  }

  // Ohne echte Marktdaten zeigen wir nichts Erfundenes — und keine
  // Dauer-Ladeanzeige, die wie ein haengendes Portal aussieht.
  if (error || !marketData) {
    return (
      <div className="text-center py-20 max-w-md mx-auto">
        <AlertCircle className="mx-auto text-muted-foreground mb-4" size={40} />
        <p className="font-semibold text-foreground">Marktdaten sind gerade nicht abrufbar</p>
        <p className="text-sm text-muted-foreground mt-2">
          Die Boersendaten stammen von externen Quellen, die derzeit nicht antworten.
          Ihr Angebot und Ihre Unterlagen sind davon nicht betroffen.
        </p>
        <button
          onClick={refetch}
          className="mt-5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  const analysis = marketData.marketAnalysis;
  const trend = marketData.trend;
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;

  // Calculate traffic light based on volatility and trend
  const trafficLight = (() => {
    const avg = analysis.avgPrice;
    const current = marketData.basePrice;
    const diff = ((current - avg) / avg) * 100;
    if (diff < -5) return { color: 'success', label: 'Guenstig', text: 'Aktueller Preis liegt deutlich unter dem Schnitt' };
    if (diff > 5) return { color: 'warning', label: 'Erhoeht', text: 'Aktueller Preis liegt ueber dem Schnitt' };
    return { color: 'info', label: 'Normal', text: 'Aktueller Preis liegt im Durchschnitt' };
  })();

  // 7-day price chart (ECharts)
  const historyData = marketData.historyData?.slice(-168) || [];
  const priceChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const p = params[0];
        return `<strong>${new Date(p.value[0]).toLocaleString('de-DE')}</strong><br/>${(p.value[1] * 100).toFixed(2)} ct/kWh`;
      }
    },
    grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
    xAxis: {
      type: 'time',
      axisLabel: { color: '#64748b', formatter: (v) => new Date(v).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) },
      axisLine: { lineStyle: { color: '#e7e9eb' } }
    },
    yAxis: {
      type: 'value',
      name: 'ct/kWh',
      axisLabel: { color: '#64748b', formatter: (v) => (v * 100).toFixed(1) },
      splitLine: { lineStyle: { color: '#f1f5f9' } }
    },
    series: [{
      name: 'EPEX Spot',
      type: 'line',
      data: historyData.map(d => [new Date(d.time), d.price]),
      smooth: true,
      symbol: 'none',
      lineStyle: { color: '#007D88', width: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(0,125,136,0.25)' },
            { offset: 1, color: 'rgba(0,125,136,0.02)' }
          ]
        }
      }
    }]
  };

  // Peak vs Off-peak
  const peakOffPeakOption = {
    tooltip: { trigger: 'axis', formatter: (params) => params.map(p => `${p.name}: ${(p.value * 100).toFixed(2)} ct/kWh`).join('<br/>') },
    grid: { left: '5%', right: '5%', bottom: '5%', top: '10%', containLabel: true },
    xAxis: { type: 'category', data: ['Peak (8-20h)', 'Off-Peak (20-8h)'], axisLine: { lineStyle: { color: '#e7e9eb' } }, axisLabel: { color: '#64748b' } },
    yAxis: { type: 'value', name: 'ct/kWh', axisLabel: { color: '#64748b', formatter: (v) => (v * 100).toFixed(1) }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
    series: [{
      type: 'bar',
      data: [
        { value: analysis.avgPeakPrice, itemStyle: { color: '#FF9100', borderRadius: [6, 6, 0, 0] } },
        { value: analysis.avgOffPeakPrice, itemStyle: { color: '#007D88', borderRadius: [6, 6, 0, 0] } },
      ],
      barWidth: '50%'
    }]
  };

  // Generate insights dynamically
  const insights = [];
  if (analysis.volatility < 0.015) {
    insights.push({ icon: CheckCircle2, color: 'success', title: 'Stabile Marktlage', text: 'Die Preisvolatilitaet ist gering. Ein guter Zeitpunkt fuer Festpreisvertraege mit Planungssicherheit.' });
  } else if (analysis.volatility > 0.025) {
    insights.push({ icon: AlertCircle, color: 'warning', title: 'Hohe Volatilitaet', text: 'Der Markt bewegt sich stark. Indexierte Tarife koennen Chancen bringen, erfordern aber Risikobereitschaft.' });
  }
  if (analysis.peakPremium > 40) {
    insights.push({ icon: Clock, color: 'info', title: 'Hoher Peak-Aufschlag', text: `Der Preisunterschied zwischen Peak- und Off-Peak-Zeiten betraegt ${analysis.peakPremium.toFixed(0)}%. Fuer Schichtbetriebe besonders attraktiv.` });
  }
  if (trend === 'down') {
    insights.push({ icon: TrendingDown, color: 'success', title: 'Fallende Preise', text: 'Der Trend zeigt nach unten — ein aktuell guenstiger Zeitpunkt fuer einen Abschluss.' });
  }
  if (data?.company?.annual_consumption_kwh >= 100000) {
    insights.push({ icon: Award, color: 'primary', title: 'RLM-Kunde', text: `Mit ${(data.company.annual_consumption_kwh / 1000).toFixed(0)} MWh Jahresverbrauch profitieren Sie von besseren Grosshandelspreisen und einer praezisen Lastganganalyse.` });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground tracking-tight">Live-Marktdaten</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Direkter Einblick in die aktuelle Lage am Energiemarkt — Daten von offiziellen Quellen
        </p>
      </div>

      {/* Hero: Current EPEX Price */}
      <div className="hk-card overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #007D88 0%, #18DDEF 100%)', color: '#fff', borderColor: 'transparent' }}>
        <div className="hk-card-body">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider opacity-80">EPEX Spot-Markt Deutschland</div>
              <div className="flex items-baseline gap-3 mt-2">
                <div className="text-6xl font-bold">{formatPriceCt(marketData.basePrice * 100)}</div>
                <div className="text-xl opacity-90">ct/kWh</div>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm">
                <TrendIcon size={16} />
                <span>{trend === 'up' ? 'Steigender Trend' : trend === 'down' ? 'Fallender Trend' : 'Stabiler Trend'}</span>
                <span className="opacity-60">&middot;</span>
                <span className="opacity-80">Letztes Update: {new Date(marketData.lastUpdated).toLocaleTimeString('de-DE')}</span>
              </div>
            </div>

            {/* Traffic Light */}
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 min-w-[180px]">
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold mb-2">Einschaetzung</div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${trafficLight.color === 'success' ? 'bg-[#00D67F]' : trafficLight.color === 'warning' ? 'bg-[#FFC400]' : 'bg-[#18DDEF]'}`} />
                <span className="text-xl font-bold">{trafficLight.label}</span>
              </div>
              <p className="text-xs opacity-90 mt-1.5 leading-tight">{trafficLight.text}</p>
            </div>
          </div>
        </div>
        <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white/5 rounded-full" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="stat-card">
          <p className="stat-label">7-Tage-Schnitt</p>
          <p className="stat-value">{formatPriceCt(analysis.avgPrice * 100)} <span className="text-base text-muted-foreground">ct</span></p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Minimum (7 Tage)</p>
          <p className="stat-value" style={{ color: '#00a864' }}>{formatPriceCt(analysis.minPrice * 100)} <span className="text-base text-muted-foreground">ct</span></p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Maximum (7 Tage)</p>
          <p className="stat-value" style={{ color: '#cc0000' }}>{formatPriceCt(analysis.maxPrice * 100)} <span className="text-base text-muted-foreground">ct</span></p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Volatilitaet</p>
          <p className="stat-value">{(analysis.volatility * 100).toFixed(1)}<span className="text-base text-muted-foreground">%</span></p>
        </div>
      </div>

      {/* Main chart */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Preisverlauf — Letzte 7 Tage</h6>
          <span className="hk-badge primary">Live</span>
        </div>
        <div className="hk-card-body">
          <div style={{ height: 360 }}>
            <ReactECharts option={priceChartOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
          </div>
        </div>
      </div>

      {/* Peak/OffPeak + Energy Mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="hk-card">
          <div className="hk-card-header">
            <h6>Peak vs. Off-Peak</h6>
          </div>
          <div className="hk-card-body">
            <div style={{ height: 240 }}>
              <ReactECharts option={peakOffPeakOption} style={{ height: '100%', width: '100%' }} opts={{ renderer: 'svg' }} />
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              Der Peak-Aufschlag betraegt aktuell <strong className="text-foreground">{analysis.peakPremium?.toFixed(0) || 0}%</strong>
            </p>
          </div>
        </div>

      </div>

      {/* Insights */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Was bedeutet das fuer Sie?</h6>
          <span className="text-xs text-muted-foreground">Experten-Einschaetzung</span>
        </div>
        <div className="hk-card-body">
          {insights.length === 0 ? (
            <p className="text-sm text-muted-foreground">Die Marktlage ist unauffaellig. Wir beobachten die Entwicklung kontinuierlich.</p>
          ) : (
            <div className="space-y-3">
              {insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg" style={{
                  background: insight.color === 'success' ? '#e6fbf2' :
                              insight.color === 'warning' ? '#fff8e0' :
                              insight.color === 'info' ? '#e8fcfd' : '#e6f2f3'
                }}>
                  <insight.icon size={18} className="flex-shrink-0 mt-0.5" style={{
                    color: insight.color === 'success' ? '#00a864' :
                           insight.color === 'warning' ? '#b8860b' :
                           insight.color === 'info' ? '#0ea5b8' : '#007D88'
                  }} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{insight.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{insight.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Data Sources — Trust Signal */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Unsere Datenquellen</h6>
          <Database size={14} className="text-muted-foreground" />
        </div>
        <div className="hk-card-body">
          <p className="text-xs text-muted-foreground mb-4">
            Unsere Marktdaten stammen aus diesen oeffentlichen Quellen. Boersenpreise werden mehrmals
            taeglich aktualisiert; kurzfristige Abweichungen zur Boerse sind moeglich.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { name: 'Energy-Charts', src: 'Fraunhofer ISE', desc: 'Oeffentliches Institut fuer Solare Energiesysteme' },
              { name: 'SMARD', src: 'Bundesnetzagentur', desc: 'Offizielle Strommarktdaten der BNetzA' },
              { name: 'aWATTar', src: 'EPEX Spot', desc: 'Live-Marktdaten der europaeischen Strombörse' },
            ].map(source => (
              <div key={source.name} className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 size={14} className="text-success" />
                  <span className="font-bold text-sm text-foreground">{source.name}</span>
                </div>
                <div className="text-xs font-semibold text-primary">{source.src}</div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{source.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalMarket;
